import { useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Loader2, Sparkles, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { PageLoader } from "@/components/ui/Spinner";
import { EmptyState, SectionCard, TableShell, Th } from "@/features/power_projects/components/ui";
import { useLeadDictionaries } from "@/features/leads/api/leads";
import {
  useCreateSearch,
  useDeleteSearch,
  useLeadSearches,
  type SearchStatus,
} from "@/features/leads/api/searches";

/**
 * Zlecenia wyszukiwania firm.
 *
 * Prompt jest celowo zwykłym polem tekstowym: użytkownik wie, kogo szuka i po co,
 * a formularz z dwudziestoma checkboxami i tak nie opisałby tego lepiej niż jedno
 * zdanie. Województwa są osobno, bo to jedyne kryterium, które potem realnie
 * filtruje bazę.
 */

const STATUS_COLORS: Record<SearchStatus, "gray" | "blue" | "green" | "red"> = {
  pending: "gray",
  running: "blue",
  done: "green",
  failed: "red",
};

export function LeadSearchPage() {
  const { data: dictionaries } = useLeadDictionaries();
  const { data: searches, isLoading } = useLeadSearches();
  const createSearch = useCreateSearch();
  const removeSearch = useDeleteSearch();

  const [prompt, setPrompt] = useState("");
  const [voivodeships, setVoivodeships] = useState<string[]>([]);
  const [maxResults, setMaxResults] = useState(20);
  const [error, setError] = useState("");

  const submit = () => {
    if (prompt.trim().length < 10) {
      setError("Napisz konkretnie, czego szukasz — jedno słowo nic modelowi nie mówi.");
      return;
    }
    setError("");
    createSearch.mutate(
      { prompt: prompt.trim(), voivodeships, max_results: maxResults },
      {
        onSuccess: () => setPrompt(""),
        onError: () => setError("Nie udało się uruchomić wyszukiwania."),
      },
    );
  };

  return (
    <>
      <SectionCard title="Nowe wyszukiwanie" className="mb-5">
        <p className="section-label mb-2">
          Napisz, jakich firm potrzebujesz i do czego. Model przeszuka internet i zwróci
          kandydatów — nic nie trafi do bazy, dopóki nie zaznaczysz, kto wchodzi.
        </p>
        <textarea
          className="input-base mb-2 min-h-[4.5rem]"
          placeholder="Np. firmy geodezyjne z uprawnieniami do obsługi linii SN, które robią mapy do celów projektowych w województwie lubelskim"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[16rem] flex-1">
            <MultiSelect
              options={dictionaries?.voivodeships ?? []}
              value={voivodeships}
              onChange={setVoivodeships}
              placeholder="Województwa (opcjonalnie)"
            />
          </div>
          <select
            className="input-base sm:w-40"
            value={maxResults}
            onChange={(e) => setMaxResults(Number(e.target.value))}
          >
            {[10, 20, 30, 50].map((count) => (
              <option key={count} value={count}>
                do {count} firm
              </option>
            ))}
          </select>
          <Button onClick={submit} loading={createSearch.isPending}>
            <Sparkles size={14} className="mr-1.5 inline" /> Szukaj
          </Button>
        </div>
        {error && <p className="mt-2 text-sm text-[var(--color-primary)]">{error}</p>}
      </SectionCard>

      {isLoading ? (
        <PageLoader />
      ) : (searches ?? []).length === 0 ? (
        <div className="card">
          <EmptyState>
            Żadnego wyszukiwania jeszcze nie uruchomiono.
          </EmptyState>
        </div>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <Th>Czego szukaliśmy</Th>
              <Th>Status</Th>
              <Th>Kandydaci</Th>
              <Th>Koszt</Th>
              <Th>Kto / kiedy</Th>
              <Th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {(searches ?? []).map((row) => {
              const running = row.status === "pending" || row.status === "running";
              return (
                <tr key={row.id} className="border-t border-[var(--color-border)] align-top">
                  <td className="px-3 py-2.5">
                    <Link
                      to={`/leads/search/${row.id}`}
                      className="text-sm text-[var(--color-text-primary)] hover:text-[var(--color-primary)]"
                    >
                      {row.prompt.length > 120 ? `${row.prompt.slice(0, 120)}…` : row.prompt}
                    </Link>
                    {row.error && (
                      <div className="mt-1 text-[11px] text-[var(--color-primary)]">{row.error}</div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <Badge color={STATUS_COLORS[row.status]}>
                      {running && <Loader2 size={10} className="mr-1 inline animate-spin" />}
                      {row.status_label}
                    </Badge>
                  </td>
                  <td className="px-3 py-2.5 font-mono">
                    {row.counts.total === 0 ? (
                      <span className="text-[var(--color-text-muted)]">—</span>
                    ) : (
                      <>
                        {row.counts.proposed > 0 && (
                          <span className="text-[var(--color-primary)]">
                            {row.counts.proposed} do przejrzenia
                          </span>
                        )}
                        {row.counts.proposed === 0 && (
                          <span className="text-[var(--color-text-muted)]">przejrzane</span>
                        )}
                        <div className="text-[11px] text-[var(--color-text-muted)]">
                          {row.counts.accepted} dodanych · {row.counts.rejected} odrzuconych
                        </div>
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-[var(--color-text-secondary)]">
                    {Number(row.cost_usd) > 0 ? `$${Number(row.cost_usd).toFixed(2)}` : "—"}
                    {row.web_searches > 0 && (
                      <div className="text-[11px] text-[var(--color-text-muted)]">
                        {row.web_searches} zapytań
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-[11px] text-[var(--color-text-muted)]">
                    {row.created_by_name ?? "—"}
                    <div>{format(new Date(row.created_at), "dd.MM.yyyy HH:mm")}</div>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button
                      type="button"
                      title="Usuń zlecenie razem z kandydatami"
                      onClick={() => {
                        if (!confirm("Usunąć to wyszukiwanie? Dodane już leady zostaną.")) return;
                        removeSearch.mutate(row.id);
                      }}
                      className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </TableShell>
      )}
    </>
  );
}
